import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import type Owner from '@ember/owner';
import { eq } from 'ember-truth-helpers';
import type Session from '../services/session';
import type RouterService from '@ember/routing/router-service';
import { apiFetch } from '../fetch';
import Icon from '../components/icon';
import CheckIcon from '~icons/lucide/check-circle';
import AlertIcon from '~icons/lucide/alert-circle';
import LinkIcon from '~icons/lucide/link';

interface TokenInfoResponse {
  data?: {
    attributes?: {
      platform?: string;
      platformUsername?: string | null;
      platformDisplayName?: string | null;
      expired?: boolean;
      used?: boolean;
    };
  };
}

export default class LinkAccountPage extends Component {
  @service declare session: Session;
  @service declare router: RouterService;

  @tracked status = 'loading';
  @tracked errorMessage = '';
  @tracked platform = '';
  @tracked platformUsername = '';
  @tracked platformDisplayName = '';
  @tracked token = '';

  constructor(owner: Owner, args: Record<string, never>) {
    super(owner, args);
    void this.loadTokenInfo();
  }

  @action
  async loadTokenInfo() {
    const params = new URLSearchParams(window.location.search);
    this.token = params.get('token') ?? '';

    if (!this.token) {
      this.status = 'error';
      this.errorMessage =
        'No link token provided. Please use the link from your chat bot.';
      return;
    }

    this.status = 'loading';

    try {
      const headers: Record<string, string> = {};
      if (this.session.authHeader) {
        headers['Authorization'] = this.session.authHeader;
      }

      const url = `/api/me/linked-accounts/token-info?token=${encodeURIComponent(this.token)}`;
      const response = await apiFetch(url, { headers });

      if (response.ok) {
        const data = (await response.json()) as TokenInfoResponse;
        const attrs = data?.data?.attributes;
        this.platform = attrs?.platform ?? 'chat';
        this.platformUsername = attrs?.platformUsername ?? '';
        this.platformDisplayName = attrs?.platformDisplayName ?? '';

        if (attrs?.expired) {
          this.status = 'error';
          this.errorMessage =
            'This link token has expired. Please request a new one from the bot (/link).';
        } else if (attrs?.used) {
          this.status = 'error';
          this.errorMessage = 'This link token has already been used.';
        } else {
          this.status = 'confirm';
        }
      } else {
        this.status = 'error';
        this.errorMessage = 'Invalid or unknown link token.';
      }
    } catch {
      this.errorMessage = 'Network error. Please try again.';
      this.status = 'error';
    }
  }

  @action
  async confirmLink() {
    this.status = 'linking';

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/vnd.api+json',
      };

      if (this.session.authHeader) {
        headers['Authorization'] = this.session.authHeader;
      }

      const response = await apiFetch('/api/me/linked-accounts/confirm', {
        method: 'POST',
        headers,
        body: JSON.stringify({ token: this.token }),
      });

      if (response.ok) {
        const data = (await response.json()) as {
          data?: { attributes?: { platform?: string } };
        };
        this.platform = data?.data?.attributes?.platform ?? this.platform;
        this.status = 'success';
      } else {
        const error = (await response.json().catch(() => null)) as {
          errors?: { title?: string; detail?: string }[];
        } | null;
        this.errorMessage =
          error?.errors?.[0]?.detail ??
          error?.errors?.[0]?.title ??
          `Linking failed (${response.status})`;
        this.status = 'error';
      }
    } catch {
      this.errorMessage = 'Network error. Please try again.';
      this.status = 'error';
    }
  }

  @action
  cancel() {
    this.router.transitionTo('explore');
  }

  @action
  goToAccount() {
    this.router.transitionTo('my-account');
  }

  <template>
    <div class="mx-auto max-w-md">
      <h1 class="mb-6 text-center text-3xl font-bold">Link Account</h1>

      <div class="card bg-base-100 shadow">
        <div class="card-body items-center text-center">
          {{#if (eq this.status "loading")}}
            <span class="loading loading-spinner loading-lg"></span>
            <p class="text-base-content/70 mt-4">Loading link details…</p>

          {{else if (eq this.status "confirm")}}
            <Icon @icon={{LinkIcon}} class="text-info h-16 w-16" />
            <h2 class="mt-4 text-xl font-semibold">Link your account?</h2>
            <p class="text-base-content/70 mt-2">
              Connect your
              <span class="font-medium capitalize">{{this.platform}}</span>
              account
              {{#if this.platformUsername}}
                <span class="font-medium">(@{{this.platformUsername}})</span>
              {{else if this.platformDisplayName}}
                <span class="font-medium">({{this.platformDisplayName}})</span>
              {{/if}}
              to your Favoris account.
            </p>
            <div class="mt-6 flex gap-3">
              <button
                type="button"
                class="btn btn-primary"
                {{on "click" this.confirmLink}}
              >
                Confirm Link
              </button>
              <button
                type="button"
                class="btn btn-outline"
                {{on "click" this.cancel}}
              >
                Cancel
              </button>
            </div>

          {{else if (eq this.status "linking")}}
            <span class="loading loading-spinner loading-lg"></span>
            <p class="text-base-content/70 mt-4">Linking your account…</p>

          {{else if (eq this.status "success")}}
            <Icon @icon={{CheckIcon}} class="text-success h-16 w-16" />
            <h2 class="mt-4 text-xl font-semibold">Account Linked!</h2>
            <p class="text-base-content/70 mt-2">
              Your
              <span class="font-medium capitalize">{{this.platform}}</span>
              account has been linked successfully. You can now send video links
              to the bot to extract mentions.
            </p>
            <button
              type="button"
              class="btn btn-primary mt-6"
              {{on "click" this.goToAccount}}
            >
              Go to My Account
            </button>

          {{else if (eq this.status "error")}}
            <Icon @icon={{AlertIcon}} class="text-error h-16 w-16" />
            <h2 class="mt-4 text-xl font-semibold">Linking Failed</h2>
            <p class="text-base-content/70 mt-2">
              {{this.errorMessage}}
            </p>
            <button
              type="button"
              class="btn btn-outline mt-6"
              {{on "click" this.goToAccount}}
            >
              Go to My Account
            </button>
          {{/if}}
        </div>
      </div>
    </div>
  </template>
}
